import { Button, Dropdown } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { typography } from '@shared/tokens/typography.stylex';
import { styles } from './styles';
import { SuccessIcon, ErrorIcon, XmarkIcon } from '@shared/assets/icons';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import EmployeeSwitcher from '@entities/employee-switcher';
import CategoryParticipantsList from '@entities/add-category-modal/category-participants-list';
import WithExcel from '@features/add-participants-modal/with-excel';
import { GuestsData, GuestsDataRaw, useAddParticipantsToCategory } from '@pages/event/api/useAddParticipantsToCategory';
import { Category } from '@pages/event/api/useFetchCategory';
import { useToast } from '@shared/hooks/useToast';
import { useRemoveTicketsFromCategory } from '@pages/event/api/useFetchDeleteTicketCategory';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import { InvitedParticipants } from '@widgets/segments-tab';
import { FreelanceEmployee } from '@features/add-participants-modal/freelance-employee';
import { IFreelanceEmployee } from '@shared/types/types';
import { FreelanceEmployeeStep, PARTICIPANT_TAB_INDEX } from '@features/add-participants-modal/model/types';
import { ErrorData, useCreateUser } from '@features/api/use-create-user';
import { cleanPhoneNumber } from '@shared/libs/clean-number';
import { useUpdateUser } from '@features/api/use-update-user';
import { CreateFreelanceResultModal } from '@entities/freelance-employee';
import { CORRECT_PHONE_LENGTH } from '@shared/constants/errorMessages';

const STATUSES_CREATE_EMPLOYEE = {
  default: '',
  success: 'success',
  error: 'error',
};

interface AddParticipantToCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId?: number;
  refetch: () => void;
  title?: string;
  eventId: number;
  participantsIds?: number[];
  newParticipantsIds?: number[];
  isCopyParticipants?: boolean;
  categories?: Category[];
  invitedParticipants?: InvitedParticipants[];
}

const AddParticipantToCategoryModal = ({
  isOpen,
  onClose,
  categoryId,
  refetch,
  eventId,
  title,
  participantsIds,
  newParticipantsIds,
  isCopyParticipants = false,
  categories,
  invitedParticipants,
}: AddParticipantToCategoryModalProps) => {
  const [status, setStatus] = useState<'default' | 'success' | 'error'>('default');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>();
  const [selectedParticipants, setSelectedParticipants] = useState<number[]>([]);
  const [guestsData, setGuestsData] = useState<GuestsData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined);
  const [participantsDelete, setParticipantsDelete] = useState<InvitedParticipants[]>([]);
  const [, addParticipantsToCategory] = useAddParticipantsToCategory();
  const [, removeTicketsFromCategory] = useRemoveTicketsFromCategory();
  const { showToast } = useToast();
  const [freelanceEmployees, setFreelanceEmployees] = useState<IFreelanceEmployee[]>([]);
  const [guestSelectedParticipants, setGuestSelectedParticipants] = useState<number[]>([]);
  const [guestParticipantsDelete, setGuestParticipantsDelete] = useState<InvitedParticipants[]>([]);
  const [freelanceStep, setFreelanceStep] = useState<FreelanceEmployeeStep>(FreelanceEmployeeStep.STRUCTURE);
  const [editableEmployee, setEditableEmployee] = useState<IFreelanceEmployee>();
  const [errorsCreateEmployee, setErrorsCreateEmployee] = useState<ErrorData[]>([]);
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [statusCreateEmployee, setStatusCreateEmployee] = useState(STATUSES_CREATE_EMPLOYEE.default);
  const { createUser } = useCreateUser();
  const { updateUser } = useUpdateUser();
  const isAddEmployee =
    activeIndex === PARTICIPANT_TAB_INDEX.FREELANCE_EMPLOYEE && freelanceStep === FreelanceEmployeeStep.FORM;

  const onCloseFeedbackModal = () => {
    setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.default);
    setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
    setFreelanceEmployees([]);
    setEditableEmployee(undefined);
  };

  const onEmailError = useCallback((hasError: boolean) => {
    setHasInvalidEmail(hasError);
  }, []);

  useEffect(() => {
    if (freelanceEmployees?.length) {
      const isValid = freelanceEmployees.every((employee) => {
        return (
          employee.firstName &&
          employee.lastName &&
          employee.firstName.trim() !== '' &&
          employee.lastName.trim() !== '' &&
          employee.phone &&
          employee.email &&
          cleanPhoneNumber(employee.phone).length >= CORRECT_PHONE_LENGTH
        );
      });
      setIsSaveButtonDisabled(!isValid || hasInvalidEmail);
    } else {
      setIsSaveButtonDisabled(false);
    }
  }, [freelanceEmployees, hasInvalidEmail]);

  const handleClose = () => {
    if (isAddEmployee) {
      setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
      setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.default);
      setFreelanceEmployees([]);
      setEditableEmployee(undefined);
    } else {
      if (status === 'success') {
        refetch();
      }
      setStatus('default');
      setSelectedParticipants([]);
      setParticipantsDelete([]);
      setActiveIndex(0);
      setGuestsData([]);
      setTotalCount(undefined);
      onClose();
    }
  };

  const onSuccessUploadExcel = (participants: number[] | GuestsData[]) => {
    if (!participants.length) return;

    if (typeof participants[0] === 'number') {
      setSelectedParticipants(participants as number[]);
      return;
    }

    setGuestsData(
      (participants as unknown as GuestsDataRaw[]).map((participant) => ({
        email: participant.email,
        phone: participant.phone,
        firstName: participant.first_name,
        lastName: participant.last_name,
      }))
    );
  };

  const formsForAddCategory: ReactNode[] = [
    <CategoryParticipantsList
      eventId={eventId}
      participantsIds={participantsIds}
      selectedParticipants={selectedParticipants}
      setSelectedParticipants={setSelectedParticipants}
      setParticipantsDelete={setParticipantsDelete}
      participantsDelete={participantsDelete}
    />,
    <WithExcel onSuccessUpload={onSuccessUploadExcel} setShowModal={() => {}} />,
    <FreelanceEmployee
      freelanceEmployees={freelanceEmployees}
      onChange={setFreelanceEmployees}
      onEmailError={onEmailError}
      guestSelectedParticipants={guestSelectedParticipants}
      setGuestSelectedParticipants={setGuestSelectedParticipants}
      guestParticipantsDelete={guestParticipantsDelete}
      setGuestParticipantsDelete={setGuestParticipantsDelete}
      step={freelanceStep}
      setStep={setFreelanceStep}
      invitedParticipants={invitedParticipants}
      errors={errorsCreateEmployee}
      editableEmployee={editableEmployee}
      setEditableEmployee={setEditableEmployee}
    />,
  ];
  const handleAddParticipants = async (categoryId: number, employeeIds: number[], guestsData: GuestsData[]) => {
    const result = await addParticipantsToCategory({ categoryId, employeeIds, guestsData });

    if (result.error) {
      setStatus('error');
      return;
    }

    const countWithoutSegment = result.data?.addParticipantsToCategory?.countWithoutSegment;
    if (countWithoutSegment && countWithoutSegment > 0) {
      showToast(`В мероприятие добавлено ${countWithoutSegment} участника без сегмента`, 'success', {
        duration: 60000,
        closable: true,
      });
    }

    setTotalCount(result.data?.addParticipantsToCategory?.total);
    setStatus('success');
  };

  const handleAddParticipantsToCategory = async () => {
    if (isAddEmployee) {
      try {
        if (freelanceEmployees) {
          const hasEmptyFields = freelanceEmployees.some(
            (employee) => !employee.firstName || !employee.lastName || !employee.phone || !employee.email
          );

          if (hasEmptyFields) {
            showToast('Поля для внештатного участника не могут быть пустыми', 'error');
            setIsSaveButtonDisabled(true);
            return;
          }

          const newFreelanceEmployees = freelanceEmployees.map((employee) => ({
            firstName: employee.firstName,
            lastName: employee.lastName,
            email: employee.email,
            phone: cleanPhoneNumber(employee.phone),
          }));

          if (editableEmployee) {
            const { data, error } = await updateUser({
              participantId: editableEmployee.id,
              input: newFreelanceEmployees[0],
            });

            if (data?.updateParticipant?.errorData?.errorText) {
              setErrorsCreateEmployee([data?.updateParticipant?.errorData]);
              return;
            }

            if (error) {
              setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.error);
              return;
            }

            setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.success);
            return;
          }

          const { data, error } = await createUser({ input: newFreelanceEmployees });

          if (data?.createParticipant.errorsData?.length || error) {
            setErrorsCreateEmployee(data?.createParticipant?.errorsData || []);
            return;
          }

          if (data?.createParticipant?.successIds) {
            const newIds = data?.createParticipant?.successIds.map(Number);
            setGuestSelectedParticipants((prev) => [...prev, ...newIds]);
          }

          setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
          setFreelanceEmployees([]);
        }
      } catch {
        setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.error);
      }
    } else {
      if (isCopyParticipants || !categoryId) return;

      const allParticipantsToDelete = [...participantsDelete, ...guestParticipantsDelete];
      const allGuests = [...guestsData, ...freelanceEmployees];
      const allParticipants = [...selectedParticipants, ...guestSelectedParticipants];

      if (!allParticipantsToDelete.length && !allParticipants.length && !allGuests.length) return;

      if (allParticipantsToDelete.length) {
        const idsToDelete = allParticipantsToDelete.map(({ ticketId }) => ticketId);
        const result = await removeTicketsFromCategory({
          input: {
            categoryId,
            ticketIds: idsToDelete,
          },
        });

        if (result.error || !result.data?.removeTicketsFromCategory.success) {
          setStatus('error');
          return;
        }
      }

      if (!allParticipants.length && !allGuests.length) {
        setStatus('success');
        return;
      }

      await handleAddParticipants(categoryId, allParticipants, allGuests);
    }
  };

  const handleCopyParticipants = async () => {
    if (!newParticipantsIds || !selectedCategory?.id || !isCopyParticipants) return;

    await handleAddParticipants(selectedCategory.id, newParticipantsIds, guestsData);
  };

  const onChangeCategory = (categoryId: number) => {
    setSelectedCategory(categories?.find(({ id }) => id === categoryId));
  };

  const rendersObj: Record<string, ReactNode> = {
    default: isCopyParticipants ? (
      <div {...stylex.props(styles.container)}>
        <p {...stylex.props(styles.header)}>Добавить в категорию</p>
        <Dropdown<Category>
          labelMessage="Категория *"
          placeholder="Выбрать категорию"
          settings={{ width: '100%' }}
          options={categories ?? []}
          itemText="title"
          itemValue="id"
          value={selectedCategory}
          changeFunc={(categoryId) => onChangeCategory(Number(categoryId[0]))}
        />
        <div {...stylex.props(styles.action, styles.actionCopyParticipants)}>
          <Button
            onClick={handleClose}
            variant="secondary"
            size="medium"
            settings={{ height: '36px', width: '85px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleCopyParticipants}
            variant="accent"
            size="medium"
            settings={{ height: '36px', width: '97px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Добавить
          </Button>
        </div>
      </div>
    ) : (
      <div {...stylex.props(styles.modalContent)}>
        <EmployeeSwitcher
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          blocks={['Орг. структура', 'Импортировать из Excel', 'Внештатный участник']}
        />
        {formsForAddCategory[activeIndex]}
        <CreateFreelanceResultModal status={statusCreateEmployee} onClose={() => onCloseFeedbackModal()} />
      </div>
    ),
    success: (
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.successInfo)}>
          <div {...stylex.props(styles.icon)}>
            <SuccessIcon />
          </div>
          <p {...stylex.props(styles.description)}>
            В категорию «{title || selectedCategory?.title}» успешно добавлено {totalCount || 0} участников
          </p>
        </div>
      </div>
    ),
    error: (
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.successInfo)}>
          <div {...stylex.props(styles.icon)}>
            <ErrorIcon />
          </div>
          <p {...stylex.props(styles.description)}>
            Во время добавления участников, произошла непредвиденная ошибка. Повторите попытку позднее
          </p>
        </div>
      </div>
    ),
  };

  if (status === 'success' || status === 'error') {
    return (
      <ModalWrapper
        active={isOpen}
        size="small"
        settingsContainer={{ height: 'auto', width: '480px' }}
        settingsHeader={{ padding: '0' }}
        buttons={[
          {
            children: <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>Закрыть</p>,
            variant: 'accent',
            onClick: handleClose,
            settings: { width: '90px' },
          },
        ]}
      >
        <div {...stylex.props(styles.headerContainer)}>
          <p {...stylex.props(styles.title)}>{status === 'success' ? 'Добавление участников' : 'Ошибка'}</p>
          <button onClick={handleClose} {...stylex.props(styles.xMarkIcon)}>
            <XmarkIcon size="24" />
          </button>
        </div>
        {rendersObj[status]}
      </ModalWrapper>
    );
  }

  if (isCopyParticipants) {
    return (
      <ModalWrapper
        active={isOpen}
        size="small"
        settingsContainer={{ height: 'auto' }}
        settingsHeader={{ width: '480px' }}
      >
        {isOpen && rendersObj[status]}
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      active={isOpen}
      title={`Добавить участников в категорию: ${title}`}
      size="small"
      settingsContainer={{ height: '554px' }}
      settingsHeader={{ width: '920px' }}
      {...stylex.props(styles.base)}
      buttons={[
        {
          children: <p {...stylex.props(styles.textBtn, styles.cancelBtn)}>Отмена</p>,
          variant: 'secondary',
          onClick: handleClose,
          settings: { width: '97px' },
        },
        {
          children: <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>Добавить</p>,
          variant: 'accent',
          onClick: handleAddParticipantsToCategory,
          settings: { width: '97px' },
          isDisabled: isSaveButtonDisabled,
        },
      ]}
    >
      {isOpen && rendersObj[status]}
    </ModalWrapper>
  );
};

export default AddParticipantToCategoryModal;
